import React, { useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate, useParams } from 'react-router-dom'
import { useCurrentUser } from '../../hooks/useCurrentUser'
import useApi from '../../hooks/useApi'
import useCommunityMapPins from '../../hooks/useCommunityMapPins'
import useFollowState from '../../hooks/useFollowState'
import useProfileMapCenter from '../../hooks/useProfileMapCenter'
import { buildSocialLinks } from '../../lib/socialLinks'
import ProfileTemplate from './ProfileTemplate'
import EventSlideshowModal from '../../components/EventSlideshowModal'
import FollowersListModal from '../../components/Profiles/FollowersListModal'
import { ProfileMain } from '../../components/Profiles'
import {
  BusinessCoverPhotosModal,
  BusinessProfileLeftSidebar,
} from '../../components/Profiles/BusinessProfile'
import EventMultiplier from '../../components/Profiles/BusinessProfile/EventMultiplier/EventMultiplier'
import Loading from '../../components/loading'

function formatBusinessLocation(business) {
  if (!business) return ''
  const parts = [business.city, business.state].filter(Boolean)
  return parts.join(', ')
}

function isBusinessPageAdmin(currentUser, business) {
  if (!currentUser?.id || !business?.id) return false
  if (currentUser.has_business && currentUser.business_id === business.id) {
    return true
  }
  if (
    business.user_id != null &&
    Number(business.user_id) === Number(currentUser.id)
  ) {
    return true
  }
  const admins = business.business_admins
  if (!Array.isArray(admins)) return false
  return admins.some(admin => Number(admin?.id) === Number(currentUser.id))
}

export default function BusinessProfilePage() {
  const { t, i18n } = useTranslation()
  const navigate = useNavigate()
  const { slugOrId } = useParams()
  const { currentUser } = useCurrentUser()
  const {
    getBusiness,
    getBusinessFollowers,
    businessFollow,
    businessUnfollow,
  } = useApi()

  const [business, setBusiness] = useState(null)
  const [isLoading, setIsLoading] = useState(true)
  const [notFound, setNotFound] = useState(false)
  const [stripeStatus, setStripeStatus] = useState(null)
  const [coverModalOpen, setCoverModalOpen] = useState(false)
  const [followersModalOpen, setFollowersModalOpen] = useState(false)

  const isMyBusinessRoute = !slugOrId
  const businessId = isMyBusinessRoute ? currentUser?.business_id : null

  useEffect(() => {
    if (isMyBusinessRoute && !businessId) {
      setIsLoading(false)
      return
    }
    if (!isMyBusinessRoute && !slugOrId) {
      setIsLoading(false)
      return
    }
    const identifier = isMyBusinessRoute ? businessId : slugOrId
    let cancelled = false
    setNotFound(false)
    setIsLoading(true)
    const run = async () => {
      try {
        const { data } = await getBusiness(identifier)
        if (cancelled) return
        if (!data) {
          setBusiness(null)
          setStripeStatus(null)
          if (!isMyBusinessRoute) setNotFound(true)
          return
        }

        const nextBusiness = { ...data, industry_id: data.industry?.id }
        setBusiness(nextBusiness)
        setStripeStatus(
          nextBusiness.verified_by_stripe
            ? { verified_by_stripe: true }
            : null,
        )
      } catch (err) {
        if (!cancelled) {
          setBusiness(null)
          setStripeStatus(null)
          if (!isMyBusinessRoute) setNotFound(true)
        }
      } finally {
        if (!cancelled) setIsLoading(false)
      }
    }
    run()
    return () => {
      cancelled = true
    }
  }, [isMyBusinessRoute, businessId, slugOrId, getBusiness])

  const locationStr = business ? formatBusinessLocation(business) : ''
  const businessIdOrSlug = business ? business.slug ?? business.id : null
  const { center: mapCenter, loading: mapCenterLoading } = useProfileMapCenter({
    location: locationStr || null,
    enabled: !!locationStr,
  })
  const { pins: mapPins } = useCommunityMapPins('business', businessIdOrSlug)

  const [slideshowEvent, setSlideshowEvent] = React.useState(null)

  const isPageAdmin = isBusinessPageAdmin(currentUser, business)

  const { isFollowing, followLoading, onFollowClick } = useFollowState({
    subjectId: !isPageAdmin ? business?.id : null,
    initialFollowing: business?.current_user_follows ?? false,
    followApi: businessFollow,
    unfollowApi: businessUnfollow,
    onSuccess: useCallback(
      nextFollowing =>
        setBusiness(prev =>
          prev
            ? {
                ...prev,
                current_user_follows: nextFollowing,
                followers_count: Math.max(
                  0,
                  (prev.followers_count ?? 0) + (nextFollowing ? 1 : -1),
                ),
              }
            : prev,
        ),
      [],
    ),
  })

  const profileUrl =
    business?.slug != null
      ? `${window.location.origin}/profile/business/${encodeURIComponent(
          business.slug,
        )}`
      : null
  const onShareClick = useCallback(() => {
    if (!profileUrl) return
    navigator.clipboard.writeText(profileUrl).then(
      () => {
        if (typeof window !== 'undefined' && window.alert) {
          window.alert(t('share.copied', 'Profile link copied to clipboard.'))
        }
      },
      () => {},
    )
  }, [profileUrl, t])

  const handleOpenSlideshow = useCallback(({ eventId, eventSlug }) => {
    setSlideshowEvent({
      id: eventId,
      slug: eventSlug || null,
    })
  }, [])

  const handleCloseSlideshow = useCallback(() => {
    setSlideshowEvent(null)
  }, [])

  const goToBusinessSettings = useCallback(() => {
    navigate('/business-account')
  }, [navigate])

  const handleCoverSaved = useCallback(coverFields => {
    setBusiness(prev => (prev ? { ...prev, ...coverFields } : prev))
  }, [])

  const fetchBusinessFollowers = useCallback(
    ({ page } = {}) => {
      if (!business?.id) return Promise.resolve(null)
      return getBusinessFollowers({ id: business.id, page })
    },
    [business?.id, getBusinessFollowers],
  )

  if (isLoading) {
    return <Loading show />
  }

  if (notFound) {
    return (
      <div className="container py-5">
        <p className="text-muted">
          {t('profiles.businessNotFound', 'Business not found.')}
        </p>
      </div>
    )
  }

  if (isMyBusinessRoute && (!businessId || !business)) {
    navigate('/business-account', { replace: true })
    return null
  }

  if (!business) {
    return null
  }

  const industryName = business.industry
    ? (i18n.language === 'es'
        ? business.industry.es_name
        : business.industry.en_name) || business.industry.en_name
    : ''
  const logoUrl =
    business.media_attachment?.thumbnail_url ??
    business.media_attachment?.direct_upload_url ??
    null
  const coverImageUrl =
    business.cover_photo?.thumbnail_url ??
    business.cover_photo?.direct_upload_url ??
    undefined
  const followersCount =
    typeof business.followers_count === 'number' ? business.followers_count : 0

  return (
    <>
      <ProfileTemplate
        profileType="business"
        headerProps={{
          coverImageUrl,
          logoUrl,
          businessName: business.name,
          onUploadLogoClick: isPageAdmin ? goToBusinessSettings : undefined,
          onEditPageClick: isPageAdmin ? goToBusinessSettings : undefined,
          onCoverPhotosClick: isPageAdmin
            ? () => setCoverModalOpen(true)
            : undefined,
          socialLinks: buildSocialLinks(business),
          isFollowing,
          onFollowClick:
            !isPageAdmin && business.id ? onFollowClick : undefined,
          followLoading,
          onShareClick: profileUrl ? onShareClick : undefined,
        }}
        breadcrumbItems={[
          { label: t('profiles.breadcrumbHome', 'CR Homepage'), href: '/' },
          { label: t('profiles.breadcrumbIndustry', 'Industry') },
          { label: business.name },
        ]}
        leftSidebarContent={
          <BusinessProfileLeftSidebar
            name={business.name}
            location={locationStr}
            industryName={industryName}
            bio={business.community_statement}
            websiteUrl={business.website_url}
            followersCount={followersCount}
            onFollowersClick={
              business.id != null
                ? () => setFollowersModalOpen(true)
                : undefined
            }
            onEditClick={isPageAdmin ? goToBusinessSettings : undefined}
            stripeStatus={stripeStatus}
            categories={business.categories}
            locale={currentUser?.language ?? i18n.language ?? 'en'}
          />
        }
        mainContent={
          <ProfileMain
            location={locationStr}
            mapCenter={mapCenter}
            mapCenterLoading={mapCenterLoading}
            profileType="business"
            mapPins={mapPins}
            onOpenSlideshow={handleOpenSlideshow}
          />
        }
        rightSidebarContent={
          <EventMultiplier
            businessId={business?.id}
            enabled={!!currentUser?.id}
            locale={currentUser?.language ?? i18n.language ?? 'en'}
          />
        }
      />
      {slideshowEvent && (
        <EventSlideshowModal
          show
          onHide={handleCloseSlideshow}
          eventId={slideshowEvent.id}
          eventSlug={slideshowEvent.slug}
        />
      )}
      {isPageAdmin ? (
        <BusinessCoverPhotosModal
          show={coverModalOpen}
          onHide={() => setCoverModalOpen(false)}
          business={business}
          onSaved={handleCoverSaved}
        />
      ) : null}
      {business.id != null ? (
        <FollowersListModal
          show={followersModalOpen}
          onHide={() => setFollowersModalOpen(false)}
          profileUserId={business.id}
          fetchFollowers={fetchBusinessFollowers}
          followersTotal={followersCount}
          currentUserId={currentUser?.id ?? null}
        />
      ) : null}
    </>
  )
}
